import React, {createContext, ReactNode, useCallback, useContext, useMemo, useState} from 'react';
import {toast} from 'react-toastify';
import {IElement} from '../incase-domain/elements';
import {TabType} from '../components/slides/form/part/tabs';
import {IValidationErrorResponse} from '../incase-domain/common/validationErrorResponse';
import {ISlide} from '../incase-domain/slide';
import {slideValidator} from '../incase-domain/validators/slide';
import {composeValidationMessage, formatErrors, mapElementErrorsToIds} from '../utils/validation';
import {ZodValidationError} from '../utils/ZodValidationError';

interface SlideErrorsContextType {
    errors: IValidationErrorResponse | undefined;
    validateSlide: (slide: ISlide, showToast?: boolean) => void;
    checkFieldError: (errorPath: string) => boolean;
    checkTabError: (tab: TabType, selectedElement?: IElement) => boolean;
    setErrors: (value: IValidationErrorResponse) => void;
    clearErrors: () => void;
}

const SlideErrorsContext = createContext<SlideErrorsContextType | undefined>(undefined);

export function SlideErrorsProvider({children}: {children: ReactNode}): JSX.Element {
    const [errors, setErrors] = useState<IValidationErrorResponse | undefined>();

    const validateSlide = useCallback((slide: ISlide, showToast?: boolean): void => {
        const validation = slideValidator.safeParse(slide);
        const error = validation.success ? null : new ZodValidationError(validation.error.errors);

        if (error) {
            let errorData: IValidationErrorResponse = {
                message: error.message,
                locale: 'toasts.validationError',
                errors: formatErrors(error.errors),
            };
            errorData = mapElementErrorsToIds(errorData, slide);
            setErrors(errorData);

            if (showToast) {
                error.errors.forEach((err) => {
                    toast.error(composeValidationMessage(err.message));
                });
            }
            throw error;
        } else {
            setErrors(undefined);
        }
    }, []);

    const checkFieldError = useCallback(
        (errorPath: string): boolean => {
            if (!errorPath || !errors?.errors?.elements) return false;

            const pathSegments = errorPath.split('.');
            const elements = errors.errors.elements as never[];

            const checkPathSegments = (arr: never[], segments: string[]): boolean => {
                if (!arr.length || !segments.length) return false;

                const [currentSegment, ...remainingSegments] = segments;
                return arr.some((el) => {
                    if (el[currentSegment] === undefined) return false;

                    if (remainingSegments.length === 0) {
                        return !!el[currentSegment];
                    }

                    return checkPathSegments(el[currentSegment], remainingSegments);
                });
            };

            return checkPathSegments(elements, pathSegments);
        },
        [errors]
    );

    const checkTabError = useCallback(
        (tab: TabType, selectedElement?: IElement): boolean => {
            if (!errors?.errors?.elements || !selectedElement) return false;

            const elementErrors =
                (errors.errors.elements as never[]).find((el: never) => el[selectedElement.id] !== undefined)?.[selectedElement.id] ?? [];

            const erroredProps = elementErrors.flatMap((err: never) => (err && typeof err === 'object' ? Object.keys(err) : []));

            return tab.properties?.some((prop) => erroredProps.includes(prop)) ?? false;
        },
        [errors]
    );

    const setErrorsValue = useCallback((value: IValidationErrorResponse): void => {
        setErrors(value);
    }, []);

    const clearErrors = useCallback((): void => {
        setErrors(undefined);
    }, []);

    const value = useMemo(
        () => ({
            errors,
            validateSlide,
            checkFieldError,
            checkTabError,
            setErrors: setErrorsValue,
            clearErrors,
        }),
        [errors, validateSlide, checkFieldError, checkTabError, setErrorsValue, clearErrors]
    );

    return <SlideErrorsContext.Provider value={value}>{children}</SlideErrorsContext.Provider>;
}

export const useSlideErrors = (): SlideErrorsContextType => {
    const context = useContext(SlideErrorsContext);
    if (!context) {
        throw new Error('useSlideErrors must be used within SlideErrorsProvider');
    }
    return context;
};
